import {CategoriesWithTagsDto} from '../common/hooks/useCategories';
import {ScreenInterest} from '../components/screens/TagSelectionScreen';
import {TagCodeNameDto} from '../generated/models/TagCodeNameDto';
import {
  EventDetailDto,
  EventDetailDtoFromJSON,
} from '../generated/models/EventDetailDto';
import {
  EventSummaryDto,
  EventSummaryDtoFromJSON,
} from '../generated/models/EventSummaryDto';

export const mapGetCategoriesResponseToScreenInterests = (
  categoriesResponse: CategoriesWithTagsDto[],
): ScreenInterest[] => {
  return categoriesResponse.flatMap(category => {
    return category.tags.map((tag: TagCodeNameDto) => ({
      tagName: tag.name,
      tagCode: tag.code,
      categoryName: category.name,
      categoryCode: category.code,
    }));
  });
};

const getCategoryForTagCode = (
  categoriesResponse: CategoriesWithTagsDto[],
  tagCode: number,
): CategoriesWithTagsDto => {
  return categoriesResponse.find(category =>
    category.tags.some(tag => tag.code === tagCode),
  )!;
};

export const mapTagCodesToScreenInterests = (
  tagCodes: number[],
  categories: CategoriesWithTagsDto[],
): ScreenInterest[] => {
  return tagCodes.map(tagCode => {
    const category = getCategoryForTagCode(categories, tagCode);
    const tag = category.tags.find(tag => tag.code === tagCode);
    return {
      tagName: tag?.name!,
      tagCode: tag?.code!,
      categoryName: category?.name,
      categoryCode: category?.code,
    };
  });
};

export const mapTagCodesTagNames = (
  tagCodes: number[],
  categories: CategoriesWithTagsDto[],
): string[] => {
  return tagCodes.map(tagCode => {
    const category = getCategoryForTagCode(categories, tagCode);
    const tag = category.tags.find(tag => tag.code === tagCode);
    return tag?.name || '';
  });
};

// ---------------------------------------------------------------------------
// Event UTC date mappers
//
// Event dates are UTC. The API used to send them without a timezone suffix
// (e.g. "2026-04-23T06:00:00"), which `new Date()` reads as LOCAL time; it now
// sends them with "Z". Reading the raw strings, before the generated FromJSON
// turns them into Dates, handles both: a suffix is added only when missing.
// (Shifting the parsed Date instead moved "Z" dates by the local offset.)
// ---------------------------------------------------------------------------

const asUtc = (value: unknown): unknown =>
  typeof value === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? `${value}Z`
    : value;

const withUtcOccurrences = (json: any) => ({
  ...json,
  occurrences: (json?.occurrences ?? []).map((occurrence: any) => ({
    ...occurrence,
    dateTimeStart: asUtc(occurrence.dateTimeStart),
    dateTimeStop: asUtc(occurrence.dateTimeStop),
  })),
});

export const mapEventDetailDto = (json: unknown): EventDetailDto =>
  EventDetailDtoFromJSON(withUtcOccurrences(json));

export const mapEventSummaryDto = (json: unknown): EventSummaryDto =>
  EventSummaryDtoFromJSON(withUtcOccurrences(json));
